/**
 * TorreMap — mapa en vivo de la Torre de control. Reutiliza el wrapper local
 * `react-leaflet-lite`, el basemap configurable (`useBasemapTile`) y los
 * builders de `mapHelpers` (createCircleMarker). NO usa react-leaflet ni Google.
 *
 * Pinta:
 *   · Repartidores con posición GPS en vivo (de useActividadData → socket).
 *   · Paradas (paquetes del día) coloreadas por estado, con filtro por capa.
 *
 * Es deliberadamente más simple que /admin/live-tracking (sin clustering):
 * la Torre da un panorama, no la herramienta de detalle. Para profundizar, el
 * header enlaza a Seguimiento en vivo.
 */
import { memo, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, useMap } from '../../../lib/react-leaflet-lite';
import 'leaflet/dist/leaflet.css';
import { useBasemapTile } from '../../../config/basemaps';
import { createCircleMarker } from '../../../utils/mapHelpers';
import useOperationFraming from '../../../hooks/useOperationFraming';

// Encuadre de arranque, provisional: `FitToPoints` lo reemplaza en cuanto llegan
// puntos del día. Importa cuando NO llegan —la jornada ya cerrada, o el kiosko de
// /control-tower abierto sobre un día sin actividad—, que es cuando el mapa se
// quedaba mirando Santiago aunque la operación fuera de otra región.
const BOOTSTRAP_VIEW = { center: [-33.4489, -70.6693], zoom: 11 };

// Los marcadores Leaflet se construyen como HTML/SVG inyectado (divIcon), donde
// var() no resuelve. Leemos el triplete RGB del token de estado en vivo y lo
// formateamos concreto; al cambiar de tema el componente re-renderiza (prop
// isDark) y reconstruye los íconos con el valor correcto.
const tokenRGB = (name, fallback) => {
  if (typeof window === 'undefined' || !window.getComputedStyle) return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? `rgb(${v.replace(/\s+/g, ', ')})` : fallback;
};

const buildStopColors = () => ({
  entregada: tokenRGB('--status-success', '#16A34A'),
  no_entregada: tokenRGB('--status-error', '#DC2626'),
  pendiente: tokenRGB('--status-warning', '#F59E0B'),
});

const CAR_SVG = (size) =>
  `<svg width="${Math.round(size * 0.6)}" height="${Math.round(
    size * 0.6,
  )}" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>`;

function driverIcon(color) {
  return createCircleMarker({
    size: 28,
    color,
    borderColor: '#fff',
    borderWidth: 2,
    glyph: CAR_SVG(28),
    glyphFont: 'svg',
    shadow: '0 2px 6px rgba(0,0,0,0.35)',
    className: 'tc-car-icon',
  });
}

function stopIcon(estado, colors) {
  return createCircleMarker({
    size: 11,
    color: colors[estado] || colors.pendiente,
    borderColor: '#fff',
    borderWidth: 2,
    shadow: '0 1px 3px rgba(0,0,0,.35)',
    className: 'tc-stop-icon',
  });
}

// Encaja el mapa al conjunto de puntos visibles la PRIMERA vez que llegan datos.
// Evita "todo Santiago vacío" si la operación está en otra región. Solo lo hace
// una vez para no robarle el encuadre al usuario en cada refresco del polling.
//
// Cuando NO hay puntos (jornada cerrada, kiosko sobre un día sin actividad) el
// respaldo lo pone `frame`: las instalaciones que el cliente declaró en
// /admin/settings?tab=locations, o el centro de su país.
function FitToPoints({ points, frame }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (fitted.current || points.length || !frame) return;
    // No se marca `fitted`: si más tarde llegan puntos reales, mandan ellos.
    if (frame.bounds) {
      map.fitBounds(frame.bounds, { padding: [40, 40], maxZoom: 13, animate: false });
    } else {
      map.setView(frame.center, frame.zoom, { animate: false });
    }
  }, [points.length, frame, map]);

  useEffect(() => {
    if (fitted.current || !points.length) return;
    fitted.current = true;
    if (points.length === 1) {
      map.setView([points[0][0], points[0][1]], 13, { animate: false });
      return;
    }
    const lats = points.map((p) => p[0]);
    const lngs = points.map((p) => p[1]);
    map.fitBounds(
      [
        [Math.min(...lats), Math.min(...lngs)],
        [Math.max(...lats), Math.max(...lngs)],
      ],
      { padding: [40, 40], maxZoom: 14, animate: false },
    );
  }, [points, map]);
  return null;
}

function TorreMap({ isDark, rutas, layers }) {
  const { t } = useTranslation();
  const tile = useBasemapTile(isDark);
  const frame = useOperationFraming();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stopColors = useMemo(() => buildStopColors(), [isDark]);

  // Un L.divIcon por estado, con identidad estable entre renders: si el icon
  // cambia de identidad, el wrapper hace marker.setIcon en CADA pin y Leaflet
  // reescribe su innerHTML. Compartir la instancia entre markers es seguro
  // (createIcon genera un elemento DOM por marker). Al cambiar el tema,
  // stopColors cambia y los pines se recolorean, como antes.
  const icons = useMemo(
    () => ({
      entregada: stopIcon('entregada', stopColors),
      no_entregada: stopIcon('no_entregada', stopColors),
      pendiente: stopIcon('pendiente', stopColors),
      driver: driverIcon(stopColors.entregada),
    }),
    [stopColors],
  );

  const driverPins = useMemo(
    () =>
      (rutas || [])
        .filter((r) => r.posicionActual)
        .map((r) => ({
          id: r.id,
          name: r.transportistaNombre,
          codigo: r.rutaCodigo,
          lat: r.posicionActual.lat,
          lng: r.posicionActual.lng,
        })),
    [rutas],
  );

  const stopPins = useMemo(() => {
    if (!layers.stops) return [];
    const out = [];
    (rutas || []).forEach((r) => {
      r.paradas.forEach((p) => {
        if (!p.geo) return;
        if (p.estado === 'entregada' && !layers.entregados) return;
        if (p.estado === 'no_entregada' && !layers.fallidos) return;
        if (p.estado === 'pendiente' && !layers.pendientes) return;
        out.push({
          id: p.id,
          numero: p.numero,
          comuna: p.comuna,
          estado: p.estado,
          lat: p.geo.lat,
          lng: p.geo.lng,
        });
      });
    });
    return out;
  }, [rutas, layers]);

  const allPoints = useMemo(
    () => [
      ...(layers.conductores ? driverPins.map((d) => [d.lat, d.lng]) : []),
      ...stopPins.map((s) => [s.lat, s.lng]),
    ],
    [driverPins, stopPins, layers.conductores],
  );

  return (
    <MapContainer
      center={BOOTSTRAP_VIEW.center}
      zoom={BOOTSTRAP_VIEW.zoom}
      zoomControl
      style={{ position: 'absolute', inset: 0, height: '100%', width: '100%', zIndex: 1 }}
    >
      <TileLayer
        key={tile.url}
        url={tile.url}
        attribution={tile.attribution}
        subdomains={tile.subdomains}
        maxZoom={tile.maxZoom}
      />
      <FitToPoints points={allPoints} frame={frame} />

      {stopPins.map((s) => (
        <Marker key={`s-${s.id}`} position={[s.lat, s.lng]} icon={icons[s.estado] || icons.pendiente}>
          <Popup>
            <div className="tc-popup">
              <strong>#{s.numero}</strong>
              {s.comuna && <span className="tc-popup__meta">{s.comuna}</span>}
              <span className="tc-popup__meta" style={{ color: stopColors[s.estado] }}>
                {s.estado === 'entregada'
                  ? t('Entregada')
                  : s.estado === 'no_entregada'
                    ? t('No entregada')
                    : t('Pendiente')}
              </span>
            </div>
          </Popup>
        </Marker>
      ))}

      {layers.conductores &&
        driverPins.map((d) => (
          <Marker key={`d-${d.id}`} position={[d.lat, d.lng]} icon={icons.driver} zIndexOffset={600}>
            <Popup>
              <div className="tc-popup">
                <strong>{d.name}</strong>
                <span className="tc-popup__meta">{d.codigo}</span>
              </div>
            </Popup>
          </Marker>
        ))}
    </MapContainer>
  );
}

export default memo(TorreMap);
